import type {
  Ancillary,
  AncillaryElement,
  BagCalculatorResponse,
} from "@jb-dotcom/api/bag-calculator";

// Service type constants
const SERVICE_TYPE_ENUM = {
  CARRY_ON: "B", // Carry-on items
  CHARGEABLE: "C", // Chargeable checked bags
  FREE_ALLOWANCE: "A", // Free baggage allowance
};

const formatPrice = (amount: number, includedLabel: string): string =>
  amount === 0 ? includedLabel : `$${amount.toFixed(0)}`;

const getFreeBagAllowance = (elements: AncillaryElement[]): number => {
  const allowance = elements.find(
    (element) =>
      element.serviceType === SERVICE_TYPE_ENUM.FREE_ALLOWANCE &&
      element.maxPieces !== undefined,
  );
  return allowance?.maxPieces ? Number.parseInt(allowance.maxPieces) : 0;
};

const getCarryOnPrice = (
  elements: AncillaryElement[],
  includedLabel: string,
): string => {
  const carryOn = elements.find(
    (element) =>
      element.serviceType === SERVICE_TYPE_ENUM.CARRY_ON &&
      (element.subCode === "0LN" || element.commercialName.includes("CARRYON")),
  );

  if (!carryOn) {
    return "N/A";
  }

  const amount = Number.parseFloat(carryOn.amount || "0");
  const maxPieces = Number.parseInt(carryOn.maxPieces ?? "0");

  return maxPieces > 0 && amount === 0
    ? includedLabel
    : formatPrice(amount, includedLabel);
};

const getBagPrice = (
  elements: AncillaryElement[],
  bagNumber: number,
  includedLabel: string,
): string => {
  // Check if bag is covered by free allowance
  const freeAllowance = getFreeBagAllowance(elements);
  if (bagNumber <= freeAllowance) {
    return includedLabel;
  }

  // Look for chargeable bag matching this occurrence
  const chargeableBag = elements.find(
    (element) =>
      element.serviceType === SERVICE_TYPE_ENUM.CHARGEABLE &&
      Number.parseInt(element.firstOccurrence ?? "0") === bagNumber,
  );

  if (!chargeableBag) {
    return "N/A";
  }

  const amount = Number.parseFloat(chargeableBag.amount || "0");
  return formatPrice(amount, includedLabel);
};

const processAncillary = (
  ancillary: Ancillary | undefined,
  includedLabel: string,
) => {
  const elements = ancillary?.ancillaryElement ?? [];

  return {
    carryOn: getCarryOnPrice(elements, includedLabel),
    firstBag: getBagPrice(elements, 1, includedLabel),
    secondBag: getBagPrice(elements, 2, includedLabel),
  };
};

export const processBagFees = (
  data: BagCalculatorResponse,
  includedLabel: string,
) => {
  const bookingData = data.ancillaries.find((a) => a.requestType === "booking");
  const checkinData = data.ancillaries.find((a) => a.requestType === "checkin");

  const booking = processAncillary(bookingData, includedLabel);
  const checkin = processAncillary(checkinData, includedLabel);

  return {
    carryOn: {
      booking: booking.carryOn,
      checkin: checkin.carryOn,
    },
    firstBag: {
      booking: booking.firstBag,
      checkin: checkin.firstBag,
    },
    personalItem: {
      booking: includedLabel,
      checkin: includedLabel,
    },
    secondBag: {
      booking: booking.secondBag,
      checkin: checkin.secondBag,
    },
  };
};
